import { createSlice } from "@reduxjs/toolkit";

const isPlainObject = (value) => {
  if (!value || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// Normalize backend user_profile into a stable, scalable shape for the frontend:
// - Ensures `chat_preferences` exists
// - Moves editable settings fields under `chat_preferences`
export const normalizeUserProfile = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const incoming = { ...raw };
  const chatPrefsRaw =
    isPlainObject(incoming.chat_preferences) ? { ...incoming.chat_preferences } : {};

  const moveToChatPrefs = [
    "preferred_response_style",
    "model_preferences",
    "overall_profile_summary",
  ];

  for (const key of moveToChatPrefs) {
    if (chatPrefsRaw[key] === undefined && incoming[key] !== undefined) {
      chatPrefsRaw[key] = incoming[key];
    }
    // Keep root clean and consistent (even if backend still sends legacy shape)
    if (incoming[key] !== undefined) delete incoming[key];
  }

  incoming.chat_preferences = chatPrefsRaw;
  return incoming;
};

const deepMergeKeepExisting = (base, patch) => {
  // Merge semantics:
  // - objects: deep merge
  // - arrays/primitives: replace when provided
  // - undefined: keep existing
  if (patch === undefined) return base;
  if (!isPlainObject(base) || !isPlainObject(patch)) {
    return patch;
  }

  const out = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    const prev = out[key];
    if (isPlainObject(prev) && isPlainObject(value)) {
      out[key] = deepMergeKeepExisting(prev, value);
    } else {
      out[key] = value;
    }
  }
  return out;
};

const initialState = {
  sessionProfile: null,
  userProfile: null,
};

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setSessionProfile(state, action) {
      state.sessionProfile = action.payload ?? null;
    },
    mergeUserProfile(state, action) {
      const normalized = normalizeUserProfile(action.payload);
      if (!normalized) return;
      state.userProfile = deepMergeKeepExisting(state.userProfile || {}, normalized);
    },
    clearProfiles(state) {
      state.sessionProfile = null;
      state.userProfile = null;
    },
  },
});

export const { setSessionProfile, mergeUserProfile, clearProfiles } =
  profileSlice.actions;

export const selectSessionProfile = (state) => state?.profile?.sessionProfile ?? null;
export const selectUserProfile = (state) => state?.profile?.userProfile ?? null;
export const selectOverallProfileSummary = (state) =>
  selectUserProfile(state)?.chat_preferences?.overall_profile_summary ?? null;

export default profileSlice.reducer;
